import {
    useCallback, useMemo, useState,
} from 'react'
import noop from 'lodash/noop'
import isEqual from 'lodash/isEqual'
import useValidation from 'app/hooks/useValidation/useValidation'
import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import useScrollToForm from 'app/hooks/useScrollToForm'
import {
    DeliveryServiceType,
    LaneFeesStatus,
} from 'app/enums/label.enums'

import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    LaneFeesDetail,
} from 'app/Apps/Pricing/Pricing.types'
import useLaneGroupContext from 'app/Apps/Pricing/LaneGroups/hooks/useLaneGroupContext'

import useStyles from './LaneFeesForm.styles'
import useLaneFeesFormContext from './LaneFeesFormContext'

type Props = {
    data?: LaneFeesDetail,
    disabled?: boolean
    setOpenReviewLaneFeesReturnConfirm?: (state: boolean) => void,
    setOpenReviewLaneFeesRejectedDialog?: (state: boolean) => void,
    getActionButtonsPanel?: ({
        value, isValid, edited,
    }: {
        value: LaneFeesDetail,
        isValid: boolean,
        edited: boolean
    }) => JSX.Element,
    onChange?: (value: LaneFeesDetail) => void,
    applyScroll?: boolean,
}
const defaultProps: Partial<Props> = {
    data: {} as LaneFeesDetail,
    disabled: false,
    setOpenReviewLaneFeesReturnConfirm: undefined,
    setOpenReviewLaneFeesRejectedDialog: undefined,
    getActionButtonsPanel: undefined,
    onChange: noop,
    applyScroll: false,
}

const LaneFeesForm = ({
    data,
    disabled,
    setOpenReviewLaneFeesReturnConfirm,
    setOpenReviewLaneFeesRejectedDialog,
    getActionButtonsPanel,
    onChange,
    applyScroll,
}: Readonly<Props>) => {
    const {
        classes,
    } = useStyles()
    const {
        getFields,
    } = useLaneFeesFormContext()

    const getAllowedValues = useEnumValues()
    const scrollToForm = useScrollToForm(String(data?.id), !applyScroll)
    const {
        currency, tempRange, deliveryServiceType,
    } = useLaneGroupContext()

    const [
        updatedValues,
        setUpdatedValues,
    ] = useState<LaneFeesDetail>(data)

    const value = useMemo(() => {
        return {
            ...data,
            ...updatedValues,
        }
    }, [
        data,
        updatedValues,
    ])

    const exists = Boolean(value?.id)
    const edited = !isEqual(data, value)

    const disabledEditingLaneFees = useMemo(() => {
        return [
            LaneFeesStatus.AWARDED,
            LaneFeesStatus.SUBMITTED,
            LaneFeesStatus.CANCELLED,
            LaneFeesStatus.REJECTED,
        ].includes(data?.laneFeesStatus)
    }, [data?.laneFeesStatus])

    const handleReviewLaneFeesReturnReason = useCallback((e) => {
        e.preventDefault()
        setOpenReviewLaneFeesReturnConfirm(true)
    }, [setOpenReviewLaneFeesReturnConfirm])

    const handleLaneFeesRejectReasonClick = useCallback((e) => {
        e.preventDefault()
        setOpenReviewLaneFeesRejectedDialog(true)
    }, [setOpenReviewLaneFeesRejectedDialog])

    const fields = useFieldsWithClassName(getFields({
        ccy: currency,
        fees: value?.fees,
        status: value?.laneFeesStatus,
        tempRange,
        originalStatus: data?.laneFeesStatus,
        editingNotAllowed: disabled || disabledEditingLaneFees,
        getAllowedValues,
        onReviewLaneFeesReturnReasonClick: handleReviewLaneFeesReturnReason,
        hasReturnReason: !!data?.returnReason,
        onLaneFeesRejectReasonClick:
            data?.rejectedReason
                ? handleLaneFeesRejectReasonClick
                : null,
        disabled,
        truckingRequired: deliveryServiceType === DeliveryServiceType.DELIVERY_ROAD,
    }), classes)

    const {
        isValid,
    } = useValidation({
        fields,
        values: value,
    })

    return (
        <div
            className={classes.root}
            ref={scrollToForm}
        >
            <DomainObjectForm
                name="LaneFees"
                onChange={(updated) => {
                    const changedValue = {
                        ...updatedValues,
                        ...updated,
                    }

                    onChange?.(changedValue)
                    setUpdatedValues(changedValue)
                }}
                value={value}
                fields={fields}
                exists={exists}
                className={classes.gridWrapper}
                disabled={disabled}
            />
            {!disabled ? getActionButtonsPanel?.({
                value,
                isValid,
                edited,
            }) : null}
        </div>
    )
}

LaneFeesForm.defaultProps = defaultProps

export default LaneFeesForm
